import { useLoaderData } from '@remix-run/react';
import type { PostInsightsData } from '@shared/dream-components/src/types';
import { useEffect, useState } from 'react';
import { usePageContext } from '~/context/usePageContext';
import { fetchPostInsights } from '~/services/fetchers/fetchPostInsights';
import type { StaticPageLoaderData } from '~/services/static_page';

type Response = {
  getPostInsights: (postIds: string[]) => Promise<void>;
  postsInsights: Record<string, PostInsightsData>;
  updatePostLikeCount: (postId: string) => void;
  likedPosts: string[];
};

const usePostsInsightsV2 = (): Response => {
  const {
    postsInsights: defaultPostInsights,
    likedPosts,
    hasLikedPostsDataFetched,
    hasPostInsightsDataFetched,
  } = useLoaderData<StaticPageLoaderData>();
  const { postsInsights, setPostsInsights } = usePageContext();
  const [isFetching, setIsFetching] = useState(false);

  // Set data in state initially
  useEffect(() => {
    if (hasPostInsightsDataFetched && hasLikedPostsDataFetched) {
      const combinedPostInsights = {
        ...postsInsights,
        ...(defaultPostInsights as Record<string, PostInsightsData>),
      };

      likedPosts.forEach((postId: string) => {
        if (combinedPostInsights[postId]) {
          combinedPostInsights[postId].isLiked = true;
        }
      });

      setPostsInsights(combinedPostInsights);
    }
  }, [hasPostInsightsDataFetched, hasLikedPostsDataFetched, likedPosts, defaultPostInsights]);

  const getPostInsights = async (postIds: string[]) => {
    const fetchablePostIds = postIds.filter((postId) => !postsInsights[postId]);

    if (fetchablePostIds.length > 0 && !isFetching) {
      setIsFetching(true);
      try {
        const response = await fetchPostInsights(fetchablePostIds);
        if (response.ok) {
          const body = await response.json();
          if ('posts' in body) {
            const newPostInsights = { ...body.posts };

            likedPosts.forEach((postId: string) => {
              if (newPostInsights[postId]) {
                newPostInsights[postId].isLiked = true;
              }
            });

            setPostsInsights({
              ...postsInsights,
              ...newPostInsights,
            });
          }
        }
      } catch {
        // do nothing
      } finally {
        setIsFetching(false);
      }
    }
  };

  return {
    getPostInsights,
    postsInsights,
    likedPosts,
    updatePostLikeCount: (postId: string) => {
      if (!postsInsights[postId]) {
        return;
      }

      const postInsight = { ...postsInsights[postId] };
      postInsight.likes = postInsight.isLiked ? postInsight.likes - 1 : postInsight.likes + 1;
      postInsight.isLiked = !postInsight.isLiked;

      setPostsInsights({
        ...postsInsights,
        [postId]: {
          ...postsInsights[postId],
          ...postInsight,
        },
      });
    },
  };
};

export default usePostsInsightsV2;
